import { useEffect, useState, type ReactNode } from "react";
import { appUpdateRequiredRef } from "@/lib/apiClient";

/**
 * US-183: once any API call reports UPDATE_REQUIRED, block the entire app
 * behind a full-screen message instead of letting individual queries fail
 * into the generic error toast — this is not a connection failure, it is a
 * device that needs an MDM-pushed update.
 *
 * `appUpdateRequiredRef` is a plain ref (not React state) set from
 * apiClient's response interceptor, which runs outside React — polled here
 * on a 1s interval rather than wired through context, since the flag only
 * ever flips forward for the lifetime of the page (once blocked, a device
 * stays blocked until reload) and no existing pub/sub exists for it.
 */
export function AppUpdateRequiredGate({ children }: { children: ReactNode }) {
  const [minVersion, setMinVersion] = useState<string | null>(
    appUpdateRequiredRef.current?.minVersion ?? null,
  );

  useEffect(() => {
    const id = setInterval(() => {
      const current = appUpdateRequiredRef.current?.minVersion ?? null;
      setMinVersion((prev) => (prev === current ? prev : current));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (!minVersion) return <>{children}</>;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 24,
      }}
    >
      <div>
        <h1>Update required</h1>
        <p>
          This iPad is running an older version of LivingSyncs than the server
          now requires. Please ask your administrator to update this device.
        </p>
      </div>
    </div>
  );
}
